import React from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import ArticlesPage from '@fuww/library/src/Layouts/ArticleListPage';
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import Head from '../components/Head';
import HomeLink from '../components/HomeLink';
import LinkAndAnchor from '../components/LinkAndAnchor';
import ArticleList from '../components/News/Article/List';
import withApollo from '../lib/apollo';
import { useSiteContext } from '../components/SiteContext';
import messages from '../lib/messages.mjs';
import authorQuery from '../lib/queries/news/authorQuery.graphql';
import getNewsboardFieldVariables
from '../lib/queries/getNewsboardFieldVariables';
import throw404 from '../lib/throw404';
import withQuery from '../lib/withQuery';
import { articleSearchImageConfigurations } from '../lib/imageConfigurations';

export const getAuthorQueryField = (hasLocalNews, prefix) => (
  hasLocalNews
    ? `${camelCase(prefix)}AuthorByEmail`
    : 'authorByEmail'
);

const getAuthorSubjectType = (fieldname) => upperFirst(
  fieldname.replace(/ByEmail$/, ''),
);

const Author = ({
  data, variables, fetchMore, loading, fieldname, showLoader,
}) => {
  const intl = useIntl();
  const { query: { authorSlug } } = useRouter();

  if (!data[fieldname]) {
    return throw404();
  }

  const {
    [fieldname]: {
      id,
      name,
    },
  } = data;

  const breadcrumbs = [
    <HomeLink />,
    intl.formatMessage(messages.news),
    <LinkAndAnchor
      route="author"
      params={{ authorSlug }}
    >
      {intl.formatMessage(messages.allArticlesBy, { author: name })}
    </LinkAndAnchor>,
  ];

  return (
    <ArticlesPage
      breadcrumbs={breadcrumbs}
      title={intl.formatMessage(messages.allArticlesBy, { author: name })}
    >
      <Head
        title={intl.formatMessage(messages.allArticlesBy, { author: name })}
        description={
          intl.formatMessage(messages.allArticlesBy, { author: name })
        }
        subjectType={getAuthorSubjectType(fieldname)}
        subjectId={id}
      />
      <ArticleList
        variables={variables}
        fetchMore={fetchMore}
        data={data}
        connectionField={fieldname}
        field="newsArticlesConnection"
        preloadFirst
        loading={loading}
        showLoader={showLoader}
      />
    </ArticlesPage>
  );
};

Author.propTypes = {
  variables: PropTypes.shape({}).isRequired,
  loading: PropTypes.bool.isRequired,
  fetchMore: PropTypes.func.isRequired,
  data: PropTypes.shape({
    authorByEmail: PropTypes.shape({
      name: PropTypes.string,
      newsArticlesConnection: PropTypes.shape({
        pageInfo: PropTypes.shape({
          hasNextPage: PropTypes.bool,
        }),
        edges: PropTypes.arrayOf(
          PropTypes.shape({}),
        ),
      }),
    }),
  }).isRequired,
  fieldname: PropTypes.string.isRequired,
  showLoader: PropTypes.bool.isRequired,
};

const AuthorWithQuery = withQuery(Author);

const AuthorPage = () => {
  const { query: { authorSlug } } = useRouter();
  const {
    prefix, hasLocalNews, newsboard,
  } = useSiteContext();

  const fieldname = getAuthorQueryField(hasLocalNews, prefix);

  return (
    <AuthorWithQuery
      query={authorQuery}
      variables={{
        ...getNewsboardFieldVariables(prefix, hasLocalNews),
        email: `${authorSlug}@fashionunited.com`,
        first: 10,
        newsboards: [newsboard],
        imageConfigurations: articleSearchImageConfigurations,
      }}
      errorMessage="Error while loading author page..."
      fieldname={fieldname}
    />
  );
};

export default withApollo(AuthorPage);
